import { useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Calculator } from "@phosphor-icons/react";
import { Field, ResultPanel, money } from "./shared";

const TILES = [
  { key: "total_prior_tax", label: "Prior tax paid", money: true },
  { key: "maximum_refund", label: "Maximum refund", money: true },
  { key: "refund_amount", label: "Refund claimable", money: true, color: "#00A86B" },
  { key: "unused_investment", label: "Not refundable", money: true, color: "#E63946" },
  { key: "net_investment_cost", label: "Net cost", money: true },
  { key: "holding_period_end", label: "Hold shares until" },
];

const CY = new Date().getFullYear();
const defaultHistory = () =>
  Array.from({ length: 6 }, (_, i) => ({ tax_year: CY - 6 + i, tax_paid: "" }));

export default function SURECalc({ onSaved }) {
  const [f, setF] = useState({ investment_amount: "", employment_end_date: "", share_issue_date: "" });
  const [history, setHistory] = useState(defaultHistory);
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setYr = (i, v) => setHistory((h) => h.map((r, idx) => (idx === i ? { ...r, tax_paid: v } : r)));
  const priorTotal = history.reduce((s, r) => s + (Number(r.tax_paid) || 0), 0);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/tax/calculate/sure", {
        investment_amount: Number(f.investment_amount),
        employment_end_date: f.employment_end_date,
        share_issue_date: f.share_issue_date,
        prior_tax_history: history.map((h) => ({ tax_year: Number(h.tax_year), tax_paid: Number(h.tax_paid || 0) })),
      });
      setCalc(r.data);
      onSaved && onSaved();
    } catch (err) { notifyError(err); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={run} className="glass rounded-2xl p-6 space-y-4">
        <div>
          <div className="eyebrow mb-1">Start-Up Relief for Entrepreneurs</div>
          <p className="text-white/55 text-sm">Refund of income tax paid in the prior 6 years when you start a new company.</p>
        </div>
        <Field label="Investment amount (€)" type="number" step="0.01" required data-testid="sure-investment" value={f.investment_amount} onChange={(e) => set("investment_amount", e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="PAYE employment ended" type="date" data-testid="sure-employment-end" value={f.employment_end_date} onChange={(e) => set("employment_end_date", e.target.value)} />
          <Field label="Share issue date" type="date" data-testid="sure-issue-date" value={f.share_issue_date} onChange={(e) => set("share_issue_date", e.target.value)} />
        </div>
        <div>
          <label>Income tax paid — prior 6 years</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {history.map((r, i) => (
              <div key={r.tax_year}>
                <div className="text-[11px] text-white/40 mb-1">{r.tax_year}</div>
                <input type="number" step="0.01" placeholder="€" data-testid={`sure-year-${i}`} value={r.tax_paid} onChange={(e) => setYr(i, e.target.value)} />
              </div>
            ))}
          </div>
          <div className="text-xs text-white/50 mt-2">Total prior tax: <span className="mono text-white/80">{money(priorTotal)}</span></div>
        </div>
        <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid="sure-calculate"><Calculator size={16} weight="fill" /> {busy ? "Calculating…" : "Calculate SURE refund"}</button>
      </form>
      {calc ? <ResultPanel calc={calc} tiles={TILES} /> : (
        <div className="glass rounded-2xl p-6 flex items-center justify-center text-white/40 text-sm text-center">Enter your prior tax history to estimate your SURE refund.</div>
      )}
    </div>
  );
}
